import React, { useState } from 'react';
import { ArrowRight, Building2, Loader2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import BusinessProfileSelector, {
    BusinessProfileErrors,
    BusinessProfileValue,
    EMPTY_BUSINESS_PROFILE,
    resolveBusinessSector,
    validateBusinessProfile,
} from '../../../components/auth/BusinessProfileSelector';
import { userErrorMessage } from '../../../lib/userErrors';
import { paidSignupApi } from '../paidSignupApi';
import { InlineError, LogoutLink, SignupShell } from '../components/SignupShell';
import { primaryButtonClass } from '../constants';

export const BusinessStep: React.FC = () => {
    const { refreshUser } = useAuth();
    const [value, setValue] = useState<BusinessProfileValue>(EMPTY_BUSINESS_PROFILE);
    const [errors, setErrors] = useState<BusinessProfileErrors>({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const validation = validateBusinessProfile(value);
        setErrors(validation);
        if (Object.keys(validation).length > 0) return;

        setSaving(true);
        setError(null);
        try {
            await paidSignupApi.updateBusinessProfile({
                business_sector: resolveBusinessSector(value),
                use_cases: value.use_cases,
                custom_use_case: value.custom_use_case?.trim() || undefined,
            });
            await refreshUser();
        } catch (err: any) {
            setError(userErrorMessage(err, 'No pudimos guardar la información de tu negocio.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <SignupShell title="Cuéntanos de tu negocio" subtitle="Así personalizamos Reveca para ti">
            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="flex items-center gap-2 p-3 bg-[#E8F7F7] rounded-xl border border-[#1CB3B3]/30">
                    <Building2 className="w-5 h-5 text-[#1CB3B3] flex-shrink-0" />
                    <p className="text-sm text-gray-700">
                        Cuéntanos sobre tu negocio para personalizar tu experiencia
                    </p>
                </div>

                <BusinessProfileSelector value={value} onChange={setValue} errors={errors} disabled={saving} />

                <InlineError message={error} />

                <button type="submit" disabled={saving} className={primaryButtonClass}>
                    {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                        <>
                            <span>Continuar</span>
                            <ArrowRight className="h-5 w-5" />
                        </>
                    )}
                </button>
            </form>
            <LogoutLink disabled={saving} />
        </SignupShell>
    );
};
